import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TipGenerator = () => {
  const [business, setBusiness] = useState("");
  const [email, setEmail] = useState("");
  const [tip, setTip] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!business.trim() || loading) return;
    setLoading(true);
    setTip("");
    try {
      const res = await fetch(`${API}/marketing-tip`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business: business.trim(), email: email.trim() }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            setTip((prev) => prev + JSON.parse(payload).t);
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setTip("The strategy desk is busy right now — please try again in a moment.");
    }
    setLoading(false);
  };

  return (
    <section data-testid="tip-generator-section" className="bg-ink text-paper px-6 md:px-12 py-24 md:py-36">
      <div className="mx-auto max-w-[100rem]">
        <Reveal>
          <p className="overline-label">07 — Free · The 30-Second Strategy Session</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl font-light tracking-tight leading-[1.05] max-w-4xl">
            Describe your business. Get <em className="text-taupe">one move</em> you
            can make this week.
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <form data-testid="tip-form" onSubmit={onSubmit} className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end">
            <div className="md:col-span-6">
              <label htmlFor="tip-business" className="text-[10px] uppercase tracking-[0.25em] text-paper/50">
                Your business, in one line
              </label>
              <input
                id="tip-business"
                data-testid="tip-business-input"
                type="text"
                required
                value={business}
                onChange={(e) => setBusiness(e.target.value)}
                placeholder="e.g. B2B SaaS selling compliance software to mid-market banks"
                className="mt-3 w-full bg-transparent border-b border-paper/30 focus:border-taupe outline-none py-3 text-base md:text-lg font-light placeholder:text-paper/30 transition-colors duration-300"
              />
            </div>
            <div className="md:col-span-4">
              <label htmlFor="tip-email" className="text-[10px] uppercase tracking-[0.25em] text-paper/50">
                Email (optional — we'll send a confirmation + the full playbook)
              </label>
              <input
                id="tip-email"
                data-testid="tip-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-3 w-full bg-transparent border-b border-paper/30 focus:border-taupe outline-none py-3 text-base md:text-lg font-light placeholder:text-paper/30 transition-colors duration-300"
              />
            </div>
            <div className="md:col-span-2">
              <button
                data-testid="tip-submit-btn"
                type="submit"
                disabled={loading}
                className="group inline-flex w-full items-center justify-center gap-2 bg-paper text-ink text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-taupe hover:text-paper disabled:opacity-50 transition-colors duration-300"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : (
                  <>Generate <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" /></>
                )}
              </button>
            </div>
          </form>
        </Reveal>

        {(tip || loading) && (
          <div data-testid="tip-output" className="mt-14 md:mt-20 border-t border-paper/20 pt-10 max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-6">Your move</p>
            <p className="font-serif italic text-2xl md:text-3xl font-light leading-relaxed whitespace-pre-wrap">
              {tip}
              {loading && <span className="inline-block w-2 h-6 ml-1 bg-taupe animate-pulse align-middle" />}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default TipGenerator;
